import api from "./api";

export const dashboardService = {
  // Get comprehensive dashboard stats
  getComprehensiveStats: async (period = "30d", startDate, endDate) => {
    try {
      const params = { period };
      // Handle single date or both dates
      // Check if at least one date is provided (not null, undefined, or empty string)
      const hasStartDate = startDate && startDate !== '';
      const hasEndDate = endDate && endDate !== '';
      
      if (hasStartDate || hasEndDate) {
        // If only startDate, set endDate to today
        // If only endDate, set startDate to a very old date (10 years ago)
        const today = new Date().toISOString().split('T')[0];
        const tenYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0];
        const finalStartDate = hasStartDate ? startDate : tenYearsAgo;
        const finalEndDate = hasEndDate ? endDate : today;
        params.start_date = finalStartDate;
        params.end_date = finalEndDate;
      }
      const response = await api.get("/api/admin/dashboard/comprehensive", {
        params,
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get comprehensive stats error:", error);
      throw error;
    }
  },

  // Get revenue trend
  getRevenueTrend: async (period = "30d", startDate, endDate) => {
    try {
      const params = { period };
      // Handle single date or both dates
      const hasStartDate = startDate && startDate !== '';
      const hasEndDate = endDate && endDate !== '';
      
      if (hasStartDate || hasEndDate) {
        const today = new Date().toISOString().split('T')[0];
        const tenYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0];
        const finalStartDate = hasStartDate ? startDate : tenYearsAgo;
        const finalEndDate = hasEndDate ? endDate : today;
        params.start_date = finalStartDate;
        params.end_date = finalEndDate;
      }
      const response = await api.get("/api/admin/dashboard/revenue-trend", {
        params,
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get revenue trend error:", error);
      throw error;
    }
  },

  // Get order status distribution
  getOrderStatusDistribution: async (period = "30d", startDate, endDate) => {
    try {
      const params = { period };
      // Handle single date or both dates
      const hasStartDate = startDate && startDate !== '';
      const hasEndDate = endDate && endDate !== '';
      
      if (hasStartDate || hasEndDate) {
        const today = new Date().toISOString().split('T')[0];
        const tenYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0];
        const finalStartDate = hasStartDate ? startDate : tenYearsAgo;
        const finalEndDate = hasEndDate ? endDate : today;
        params.start_date = finalStartDate;
        params.end_date = finalEndDate;
      }
      const response = await api.get("/api/admin/dashboard/order-status", {
        params,
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get order status distribution error:", error);
      throw error;
    }
  },

  // Get sales by category
  getSalesByCategory: async (
    period = "30d",
    limit = 10,
    startDate,
    endDate
  ) => {
    try {
      const params = { period, limit };
      // Handle single date or both dates
      const hasStartDate = startDate && startDate !== '';
      const hasEndDate = endDate && endDate !== '';
      
      if (hasStartDate || hasEndDate) {
        const today = new Date().toISOString().split('T')[0];
        const tenYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0];
        const finalStartDate = hasStartDate ? startDate : tenYearsAgo;
        const finalEndDate = hasEndDate ? endDate : today;
        params.start_date = finalStartDate;
        params.end_date = finalEndDate;
      }
      const response = await api.get("/api/admin/dashboard/sales-by-category", {
        params,
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get sales by category error:", error);
      throw error;
    }
  },

  // Get top products
  getTopProducts: async (period = "30d", limit = 10, startDate, endDate) => {
    try {
      const params = { period, limit };
      // Handle single date or both dates
      const hasStartDate = startDate && startDate !== '';
      const hasEndDate = endDate && endDate !== '';
      
      if (hasStartDate || hasEndDate) {
        const today = new Date().toISOString().split('T')[0];
        const tenYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0];
        const finalStartDate = hasStartDate ? startDate : tenYearsAgo;
        const finalEndDate = hasEndDate ? endDate : today;
        params.start_date = finalStartDate;
        params.end_date = finalEndDate;
      }
      const response = await api.get("/api/admin/dashboard/top-products", {
        params,
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get top products error:", error);
      throw error;
    }
  },

  // Get recent orders
  getRecentOrders: async (limit = 5) => {
    try {
      const response = await api.get("/api/admin/dashboard/recent-orders", {
        params: { limit },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get recent orders error:", error);
      throw error;
    }
  },

  // Get low stock products
  getLowStockProducts: async (threshold = 10) => {
    try {
      const response = await api.get("/api/inventory/low-stock", {
        params: { threshold },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get low stock products error:", error);
      throw error;
    }
  },

  // Get top delivery boys
  getTopDeliveryBoys: async (limit = 10) => {
    try {
      const response = await api.get("/api/admin/dashboard/top-delivery-boys", {
        params: { limit },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get top delivery boys error:", error);
      throw error;
    }
  },

  // Get top performing vendors
  getTopVendors: async (limit = 10) => {
    try {
      const response = await api.get("/api/admin/dashboard/top-vendors", {
        params: { limit },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get top vendors error:", error);
      throw error;
    }
  },

  // Get newly joined vendors
  getNewVendors: async (limit = 10) => {
    try {
      const response = await api.get("/api/admin/dashboard/new-vendors", {
        params: { limit },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get new vendors error:", error);
      throw error;
    }
  },

  // Get recent customer reviews/feedback
  getRecentReviews: async (limit = 15, rating = "all") => {
    try {
      const response = await api.get("/api/admin/dashboard/recent-reviews", {
        params: { limit, rating },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get recent reviews error:", error);
      throw error;
    }
  },

  // Get inventory overview
  getInventoryOverview: async () => {
    try {
      const response = await api.get("/api/inventory/overview");
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get inventory overview error:", error);
      throw error;
    }
  },

  // Vendor dashboard stats
  getVendorDashboardStats: async () => {
    try {
      const response = await api.get("/api/vendor/dashboard/stats");
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get vendor dashboard stats error:", error);
      throw error;
    }
  },

  // Get vendor revenue trend
  getVendorRevenueTrend: async (period = "30d", startDate, endDate) => {
    try {
      const params = { period };
      const hasStartDate = startDate && startDate !== '';
      const hasEndDate = endDate && endDate !== '';
      
      if (hasStartDate || hasEndDate) {
        const today = new Date().toISOString().split('T')[0];
        const tenYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0];
        const finalStartDate = hasStartDate ? startDate : tenYearsAgo;
        const finalEndDate = hasEndDate ? endDate : today;
        params.start_date = finalStartDate;
        params.end_date = finalEndDate;
      }
      const response = await api.get("/api/vendor/dashboard/revenue-trend", {
        params,
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get vendor revenue trend error:", error);
      throw error;
    }
  },

  // Get vendor order status distribution
  getVendorOrderStatusDistribution: async (period = "30d", startDate, endDate) => {
    try {
      const params = { period };
      const hasStartDate = startDate && startDate !== '';
      const hasEndDate = endDate && endDate !== '';
      
      if (hasStartDate || hasEndDate) {
        const today = new Date().toISOString().split('T')[0];
        const tenYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0];
        const finalStartDate = hasStartDate ? startDate : tenYearsAgo;
        const finalEndDate = hasEndDate ? endDate : today;
        params.start_date = finalStartDate;
        params.end_date = finalEndDate;
      }
      const response = await api.get("/api/vendor/dashboard/order-status", {
        params,
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get vendor order status distribution error:", error);
      throw error;
    }
  },

  // Get vendor top products
  getVendorTopProducts: async (period = "30d", limit = 10, startDate, endDate) => {
    try {
      const params = { period, limit };
      const hasStartDate = startDate && startDate !== '';
      const hasEndDate = endDate && endDate !== '';
      
      if (hasStartDate || hasEndDate) {
        const today = new Date().toISOString().split('T')[0];
        const tenYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 10)).toISOString().split('T')[0];
        const finalStartDate = hasStartDate ? startDate : tenYearsAgo;
        const finalEndDate = hasEndDate ? endDate : today;
        params.start_date = finalStartDate;
        params.end_date = finalEndDate;
      }
      const response = await api.get("/api/vendor/dashboard/top-products", {
        params,
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get vendor top products error:", error);
      throw error;
    }
  },

  // Get vendor recent orders
  getVendorRecentOrders: async (limit = 5) => {
    try {
      const response = await api.get("/api/vendor/dashboard/recent-orders", {
        params: { limit },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get vendor recent orders error:", error);
      throw error;
    }
  },

  // Get vendor low stock products
  getVendorLowStockProducts: async (limit = 5) => {
    try {
      const response = await api.get("/api/vendor/dashboard/low-stock-products", {
        params: { limit },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error("Get vendor low stock products error:", error);
      throw error;
    }
  },
};

export default dashboardService;
